import * as React from "react";
import { Loader2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  formatSegmentKeyDisplay,
  humanizeManagementActions,
} from "../../utils/managementActionLabels";
import type { ManagementSegmentCatalogEntry } from "../../types/tradeManagementReport";

export type ManagementPolicyInlineStripProps = {
  entry: ManagementSegmentCatalogEntry;
  isTesCandidate?: boolean;
  segmentKey?: string;
  previewLoading?: boolean;
  onPreview?: () => void;
  onEditInSimulator?: () => void;
};

export function ManagementPolicyInlineStrip(props: ManagementPolicyInlineStripProps): React.ReactElement {
  const actions = humanizeManagementActions(props.entry.management_actions);
  const rationale = (props.entry.action_rationale ?? []).filter((line) => String(line ?? "").trim());
  const segmentLabel = formatSegmentKeyDisplay(
    props.segmentKey ?? props.entry.segment_key ?? ""
  );

  return (
    <div
      data-management-policy-inline-strip=""
      className="mt-4 w-full min-w-0 rounded-[6px] border border-border bg-muted/30 px-4 py-3 font-[family-name:var(--font-primary)]"
    >
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-[color:var(--brand-tropical-surf)]">
          Trade management policy
        </span>
        {segmentLabel ? (
          <span className="text-xs text-muted-foreground">{segmentLabel}</span>
        ) : null}
        {props.isTesCandidate ? (
          <Badge
            variant="outline"
            className="border-[color:var(--brand-tropical-surf)] text-[color:var(--brand-tropical-surf)]"
          >
            TES candidate
          </Badge>
        ) : null}
      </div>

      {actions.length ? (
        <ul className="mb-2 list-disc space-y-0.5 pl-5 text-sm text-foreground">
          {actions.map((label) => (
            <li key={label}>{label}</li>
          ))}
        </ul>
      ) : (
        <p className="mb-2 text-sm text-muted-foreground">No management actions in catalog.</p>
      )}

      {rationale.length ? (
        <ul className="mb-3 list-disc space-y-0.5 pl-5 text-xs text-muted-foreground">
          {rationale.map((line, i) => (
            <li key={`${i}-${line.slice(0, 24)}`}>{line}</li>
          ))}
        </ul>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        {props.onPreview ? (
          <Button
            type="button"
            variant="gradient"
            size="sm"
            data-font="primary"
            className="h-8 shrink-0 px-5 active:translate-y-0"
            disabled={props.previewLoading}
            onClick={props.onPreview}
          >
            {props.previewLoading ? (
              <>
                <Loader2 className="mr-1.5 size-3.5 animate-spin" aria-hidden />
                <span className="uppercase tracking-wide">Preview</span>
              </>
            ) : (
              <span className="uppercase tracking-wide">Preview</span>
            )}
          </Button>
        ) : null}
        {props.onEditInSimulator ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-font="primary"
            className="h-8 shrink-0 px-5 font-[family-name:var(--font-primary)] text-sm font-normal normal-case tracking-normal"
            onClick={props.onEditInSimulator}
          >
            Edit in simulator
          </Button>
        ) : null}
      </div>
    </div>
  );
}
